<template>
  <div class="nhl-scores-wrap">
    <div class="nhl-scores-header">
      <div class="nhl-scores-heading">
        <div class="nhl-scores-title-row">
          <span class="nhl-scores-icon-wrap" aria-hidden="true">
            <i class="fa fa-hockey-puck"></i>
          </span>
          <span class="nhl-scores-title">NHL Intelligence</span>
          <span class="nhl-scores-live-badge" v-if="!loading && hasLiveGames">
            <span class="nhl-live-pulse"></span> LIVE
          </span>
        </div>
        <span class="nhl-scores-kicker">Box score with a take, straight from the barbershop</span>
      </div>
      <div class="nhl-scores-meta">
        <span v-if="dateLabel" class="nhl-scores-date">
          <i class="fa fa-calendar-days" aria-hidden="true"></i>
          {{ dateLabel }}
        </span>
        <button
          class="nhl-scores-toggle"
          @click="expanded = !expanded"
          :aria-expanded="String(expanded)"
          aria-controls="nhl-games-list"
        >
          <i :class="expanded ? 'fa fa-chevron-up' : 'fa fa-chevron-down'" aria-hidden="true"></i>
          {{ expanded ? 'Hide' : 'Show' }}
        </button>
      </div>
    </div>

    <div class="nhl-date-row">
      <label class="nhl-date-label" for="nhl-games-date">Date</label>
      <input
        id="nhl-games-date"
        v-model="selectedDate"
        class="nhl-date-input"
        type="date"
        :max="todayDate"
        @change="loadGames"
      />
    </div>

    <div v-if="loading && expanded" class="carousel-wrapper">
      <div class="carousel-track">
        <div class="carousel-slide" v-for="n in 3" :key="n">
          <div class="nhl-skeleton">
            <div class="skel-line skel-line--short"></div>
            <div class="skel-line"></div>
            <div class="skel-line skel-line--med"></div>
            <div class="skel-line skel-line--short"></div>
          </div>
        </div>
      </div>
    </div>

    <div v-else-if="error && expanded" class="nhl-error">
      <i class="fa fa-triangle-exclamation" aria-hidden="true"></i>
      Could not load today's games. Try refreshing.
    </div>

    <div v-else-if="expanded && !loading && games.length === 0" class="nhl-empty">
      <i class="fa fa-moon" aria-hidden="true"></i>
      No NHL games scheduled today.
    </div>

    <div
      v-if="expanded && !loading && games.length"
      class="carousel-wrapper"
      id="nhl-games-list"
      aria-live="polite"
    >
      <button
        class="carousel-nav carousel-nav-left"
        @click="scrollCarousel(-1)"
        aria-label="Scroll left"
      >
        <i class="fa fa-chevron-left" aria-hidden="true"></i>
      </button>

      <div
        ref="carouselTrack"
        class="carousel-track"
        role="region"
        aria-label="NHL games carousel"
      >
        <div
          class="carousel-slide"
          v-for="game in games"
          :key="game.gameId"
        >
          <NhlGameCard :game="game" />
        </div>
      </div>

      <button
        class="carousel-nav carousel-nav-right"
        @click="scrollCarousel(1)"
        aria-label="Scroll right"
      >
        <i class="fa fa-chevron-right" aria-hidden="true"></i>
      </button>
    </div>
  </div>
</template>

<script>
import NhlGameCard from './NhlGameCard.vue';
import NhlSportsService from '@/services/NhlSportsService';

const POLL_MS = 30000;

function isGameLive(game) {
  const status = String(game?.statusType || game?.status || '').toUpperCase();
  return status === 'LIVE' || status === 'IN';
}

export default {
  name: 'NhlScores',
  components: { NhlGameCard },
  data() {
    const today = new Date().toLocaleDateString('en-CA');
    return {
      games: [],
      loading: false,
      error: false,
      expanded: true,
      dateLabel: '',
      selectedDate: today,
      todayDate: today,
      pollInterval: null,
    };
  },
  computed: {
    hasLiveGames() {
      return this.games.some(isGameLive);
    },
    shouldPoll() {
      return this.selectedDate === this.todayDate
        && this.hasLiveGames
        && this.expanded;
    },
  },
  watch: {
    shouldPoll: {
      immediate: true,
      handler(shouldPoll) {
        if (shouldPoll) this.startPolling();
        else this.stopPolling();
      },
    },
    selectedDate() {
      this.syncPolling();
    },
    expanded() {
      this.syncPolling();
    },
  },
  async mounted() {
    await this.loadGames();
  },
  beforeUnmount() {
    this.stopPolling();
  },
  methods: {
    syncPolling() {
      if (this.shouldPoll) this.startPolling();
      else this.stopPolling();
    },
    startPolling() {
      if (this.pollInterval) return;
      this.pollInterval = setInterval(() => {
        if (this.shouldPoll) this.loadGames(true);
      }, POLL_MS);
    },
    stopPolling() {
      if (!this.pollInterval) return;
      clearInterval(this.pollInterval);
      this.pollInterval = null;
    },
    async loadGames(silent = false) {
      if (!silent) {
        this.loading = true;
        this.error = false;
      }
      try {
        const response = await NhlSportsService.getGames(this.selectedDate);
        const data = response.data;
        this.games = data?.games || [];
        this.dateLabel = this.formatDateLabel(data?.date || this.selectedDate);
      } catch {
        if (!silent) this.error = true;
      } finally {
        if (!silent) this.loading = false;
      }
    },
    formatDateLabel(dateValue) {
      if (!dateValue) return '';
      try {
        return new Date(`${dateValue}T00:00:00`).toLocaleDateString('en-US', {
          weekday: 'short',
          month: 'short',
          day: 'numeric',
        });
      } catch {
        return dateValue;
      }
    },
    scrollCarousel(direction) {
      const track = this.$refs.carouselTrack;
      if (!track) return;
      const scrollAmount = track.clientWidth * 0.75;
      track.scrollBy({ left: direction * scrollAmount, behavior: 'smooth' });
    },
  },
};
</script>

<style scoped>
.nhl-scores-wrap {
  font-family: 'Titillium Web', sans-serif;
  margin-bottom: 16px;
  background: #f8fbff;
  border: 1px solid #cbd5e1;
  border-top: 3px solid #0ea5e9;
  border-radius: 14px;
  padding: 14px;
}

.nhl-scores-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
  padding: 0 2px;
  margin-bottom: 12px;
  flex-wrap: wrap;
}
.nhl-scores-heading {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}
.nhl-scores-title-row {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}
.nhl-scores-icon-wrap {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: #0f172a;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.nhl-scores-icon-wrap i {
  color: #fff;
  font-size: 14px;
}
.nhl-scores-title {
  font-size: 16px;
  font-weight: 900;
  letter-spacing: 0.3px;
  color: #111827;
  line-height: 1;
}
.nhl-scores-live-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  background: #dc2626;
  color: #fff;
  font-size: 9px;
  font-weight: 900;
  letter-spacing: 1px;
  padding: 2px 7px;
  border-radius: 20px;
  text-transform: uppercase;
}
.nhl-live-pulse {
  width: 5px;
  height: 5px;
  background: #fff;
  border-radius: 50%;
  animation: pulse-dot 1.2s infinite;
  flex-shrink: 0;
}
@keyframes pulse-dot {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.15; }
}

.nhl-scores-kicker {
  font-size: 11px;
  color: #475569;
  font-weight: 600;
  letter-spacing: 0.2px;
}
.nhl-scores-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}
.nhl-scores-date {
  font-size: 11px;
  color: #334155;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 4px;
}
.nhl-scores-date i { font-size: 10px; }
.nhl-scores-toggle {
  background: #0ea5e9;
  border: none;
  cursor: pointer;
  font-size: 12px;
  color: #fff;
  font-family: inherit;
  font-weight: 800;
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 6px 12px;
  border-radius: 8px;
  transition: background 0.15s, transform 0.12s;
  white-space: nowrap;
}
.nhl-scores-toggle:hover { background: #0284c7; transform: translateY(-1px); }
.nhl-scores-toggle:focus-visible { outline: 2px solid #0ea5e9; outline-offset: 2px; }

.nhl-date-row {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  margin-bottom: 12px;
}
.nhl-date-label {
  font-size: 11px;
  font-weight: 700;
  color: #334155;
  text-transform: uppercase;
  letter-spacing: 0.7px;
}
.nhl-date-input {
  font-family: inherit;
  font-size: 12px;
  padding: 6px 10px;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  background: #ffffff;
  color: #374151;
}
.nhl-date-input:focus-visible {
  outline: 2px solid #0ea5e9;
  outline-offset: 1px;
}

.carousel-wrapper {
  position: relative;
  width: 100%;
}
.carousel-track {
  display: flex;
  overflow-x: auto;
  overflow-y: hidden;
  scroll-snap-type: x mandatory;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  -ms-overflow-style: none;
  gap: 10px;
  padding: 4px 40px 8px;
  scroll-behavior: smooth;
}
.carousel-track::-webkit-scrollbar { display: none; }
.carousel-slide {
  flex: 0 0 auto;
  width: 290px;
  scroll-snap-align: start;
}

.carousel-nav {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: #ffffff;
  color: #0ea5e9;
  border: 1.5px solid #cbd5e1;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  cursor: pointer;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.85em;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}
.carousel-nav:hover {
  background: #0ea5e9;
  color: #fff;
  border-color: #0ea5e9;
  transform: translateY(-50%) scale(1.05);
}
.carousel-nav:focus-visible {
  outline: 2px solid #0ea5e9;
  outline-offset: 2px;
}
.carousel-nav-left  { left: 0; }
.carousel-nav-right { right: 0; }

.nhl-skeleton {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 290px;
}
.skel-line {
  height: 11px;
  background: linear-gradient(90deg, #e2e8f0 0%, #f1f5f9 50%, #e2e8f0 100%);
  background-size: 200% 100%;
  border-radius: 6px;
  animation: shimmer 1.5s infinite;
}
.skel-line--short { width: 50%; }
.skel-line--med { width: 75%; }
@keyframes shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

.nhl-error,
.nhl-empty {
  font-size: 13px;
  color: #64748b;
  padding: 16px;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
}
.nhl-error i { color: #ef4444; }

@media (max-width: 480px) {
  .carousel-slide { width: 260px; }
  .nhl-skeleton { width: 260px; }
  .carousel-track { padding: 4px 32px 8px; }
  .carousel-nav { width: 30px; height: 30px; font-size: 0.75em; }
}
</style>